import type { MetadataExtractor } from "@ai-sdk/openai-compatible";
import type { AiBillingStep } from "@/lib/ai/pricing";
import type { AiMediaUsage } from "./media-pricing";
import { INPUT_MEDIA_FIELDS } from "./media-pricing";

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function count(value: unknown): number | undefined {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0 ? value : undefined;
}

/** Gemini's modality arrays are complete breakdowns; absent arrays remain unknown. */
function modalityCounts(value: unknown): Record<string, number> | undefined {
  if (!Array.isArray(value)) return undefined;
  const totals: Record<string, number> = { IMAGE: 0, AUDIO: 0, VIDEO: 0 };
  for (const raw of value) {
    const item = record(raw);
    const modality = String(item.modality ?? "").toUpperCase();
    const tokens = count(item.tokenCount ?? item.token_count ?? item.tokens);
    if (tokens === undefined) return undefined;
    totals[modality] = (totals[modality] ?? 0) + tokens;
    if (!Number.isSafeInteger(totals[modality])) return undefined;
  }
  return totals;
}

export function rawBillingUsage(value: unknown): Record<string, number> {
  const body = record(value);
  const usage = record(body.usage);
  const native = record(body.usageMetadata ?? body.usage_metadata);
  const details = record(usage.prompt_tokens_details ?? usage.input_tokens_details);
  const cachedTokens = count(details.cached_tokens) ?? count(usage.prompt_cache_hit_tokens) ?? count(usage.cache_read_input_tokens) ?? count(native.cachedContentTokenCount ?? native.cached_content_token_count);
  const cacheWriteTokens = count(details.cache_write_tokens) ?? count(details.cache_creation_tokens) ?? count(usage.cache_creation_tokens) ?? count(usage.cache_creation_input_tokens);
  const prompt = count(usage.prompt_tokens);
  const nativeInput = count(usage.input_tokens);
  const nativeCache = usage.cache_read_input_tokens !== undefined || usage.cache_creation_input_tokens !== undefined;
  const inputTokens = prompt ?? (nativeInput === undefined ? count(native.promptTokenCount ?? native.prompt_token_count) : nativeInput + (nativeCache ? (cachedTokens ?? 0) + (cacheWriteTokens ?? 0) : 0));
  const candidates = count(native.candidatesTokenCount ?? native.candidates_token_count);
  const outputTokens = count(usage.completion_tokens) ?? count(usage.output_tokens) ?? (candidates === undefined ? undefined : candidates + (count(native.thoughtsTokenCount ?? native.thoughts_token_count) ?? 0));
  const webSearchCalls = count(record(usage.web_search_usage).tool_usage);
  const outputDetails = record(usage.completion_tokens_details ?? usage.output_tokens_details);
  const cacheDetails = record(details.cached_tokens_details);
  const writeDetails = record(details.cache_write_tokens_details);
  const inputBreakdown = modalityCounts(native.promptTokensDetails ?? native.prompt_tokens_details);
  const cachedBreakdown = modalityCounts(native.cacheTokensDetails ?? native.cache_tokens_details);
  const outputBreakdown = modalityCounts(native.candidatesTokensDetails ?? native.candidates_tokens_details);
  const media: Record<string, number | undefined> = {};
  for (const [modality, fields] of Object.entries(INPUT_MEDIA_FIELDS)) {
    const name = modality.split("_")[0];
    media[fields.tokens] = count(details[`${name}_tokens`]) ?? inputBreakdown?.[name.toUpperCase()];
    media[fields.cached] = count(cacheDetails[`${name}_tokens`]) ?? cachedBreakdown?.[name.toUpperCase()];
    media[fields.write] = count(writeDetails[`${name}_tokens`]);
  }
  const outputImageTokens = count(outputDetails.image_tokens) ?? outputBreakdown?.IMAGE;
  const outputAudioTokens = count(outputDetails.audio_tokens) ?? outputBreakdown?.AUDIO;
  const outputVideoTokens = count(outputDetails.video_tokens) ?? outputBreakdown?.VIDEO;
  return Object.fromEntries(Object.entries({ inputTokens, outputTokens, cachedTokens, cacheWriteTokens, webSearchCalls, ...media, outputImageTokens, outputAudioTokens, outputVideoTokens }).filter((entry): entry is [string, number] => entry[1] !== undefined));
}

function metadata(billing: Record<string, number>) {
  return Object.keys(billing).length ? { billing } : undefined;
}

export const billingMetadataExtractor: MetadataExtractor = {
  async extractMetadata({ parsedBody }) {
    return metadata(rawBillingUsage(parsedBody));
  },
  createStreamExtractor() {
    let billing: Record<string, number> = {};
    return {
      processChunk(chunk) { billing = { ...billing, ...rawBillingUsage(chunk) }; },
      buildMetadata() { return metadata(billing); },
    };
  },
};

export function createBillingUsageTracker() {
  const steps: AiBillingStep[] = [];
  let webSearchCalls = 0;
  let hostedCalls = new Set<string>();
  return {
    steps,
    get webSearchCalls() { return webSearchCalls + hostedCalls.size; },
    toolResult(id: string) { hostedCalls.add(id); },
    finishStep(usage: { inputTokens?: number; outputTokens?: number; inputTokenDetails?: { cacheReadTokens?: number; cacheWriteTokens?: number } }, metadata: unknown, measuredMedia?: AiMediaUsage) {
      const billing = record(record(metadata).billing);
      const inputTokens = count(billing.inputTokens) ?? count(usage.inputTokens) ?? 0;
      const inputTokensReported = count(billing.inputTokens) !== undefined || count(usage.inputTokens) !== undefined;
      const outputTokensReported = count(billing.outputTokens) !== undefined || count(usage.outputTokens) !== undefined;
      const cachedTokens = Math.min(inputTokens, count(billing.cachedTokens) ?? count(usage.inputTokenDetails?.cacheReadTokens) ?? 0);
      const cacheWriteTokens = Math.min(inputTokens - cachedTokens, count(billing.cacheWriteTokens) ?? count(usage.inputTokenDetails?.cacheWriteTokens) ?? 0);
      const mediaTokens = Object.fromEntries(["outputImageTokens", "outputAudioTokens", "outputVideoTokens", ...Object.values(INPUT_MEDIA_FIELDS).flatMap(fields => [fields.tokens, fields.cached, fields.write])].flatMap(key => count(billing[key]) === undefined ? [] : [[key, count(billing[key])]]));
      const media: AiMediaUsage = { ...measuredMedia, ...mediaTokens };
      // Audio carried by a video belongs to that video file, never a second audio file.
      if (media.inputVideos?.length && media.inputAudios?.length === 0) {
        for (const [audio, video] of [["inputAudioTokens", "inputVideoTokens"], ["cachedAudioTokens", "cachedVideoTokens"], ["cacheWriteAudioTokens", "cacheWriteVideoTokens"]] as const) {
          if (media[video] !== undefined && media[audio] !== undefined) media[video] += media[audio];
          else if (media[audio] !== 0) delete media[video];
          media[audio] = 0;
        }
      }
      const step: AiBillingStep & { cacheWriteTokens: number } = { inputTokens, outputTokens: count(billing.outputTokens) ?? count(usage.outputTokens) ?? 0, cachedTokens, cacheWriteTokens,
        ...(!inputTokensReported ? { inputTokensReported: false } : {}),
        ...(!outputTokensReported ? { outputTokensReported: false } : {}),
        ...(Object.keys(media).length ? { media } : {}) };
      steps.push(step);
      webSearchCalls += count(billing.webSearchCalls) ?? hostedCalls.size;
      hostedCalls = new Set();
      return step;
    },
  };
}

export function isMimoModel(modelId: string): boolean {
  return /^(?:xiaomi[-/])?mimo-/i.test(modelId);
}
